import { apiGetAnalytics, apiGetQuickStats } from '../utils/api';
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { formatBytes } from '../utils/crypto';
import { CURRENCY_SYMBOLS } from '../utils/pricing';
import {
  apiAdminUsers, apiAdminStats, apiAdminPayments,
  apiAdminChangePlan, apiAdminPlanChangeLog, apiAdminBan, apiAdminUnban,
  apiAdminSetRole, apiAdminDeleteUser, apiAdminPaymentReminder,
  apiAdminLogins, apiAdminEmails, apiAdminTickets, apiAdminTicketGet, apiAdminTicketReply, apiAdminTicketStatus,
  apiAdminLiveVisits, apiAdminUploadStatus, apiAdminGetMaintenance, apiAdminSetMaintenance,
  apiAdminStackSocialCodes, apiAdminStackSocialTrend, apiAdminStackSocialLookup, apiAdminStackSocialApply,
  apiAdminPosti, apiAdminPostiAggiungi, apiAdminPostiImposta,
  apiTicketAttachment, apiTicketAttachmentUrl,
} from '../utils/api';

const TICKET_STATUSES = [
  ['open', '🟡 Open'],
  ['in_progress', '🔵 In progress'],
  ['answered', '🟢 Answered'],
  ['closed', '⚪ Closed'],
];

// Helper to get plan ID whether plan is string or object
const getPlanId = (plan) => (typeof plan === 'object' && plan !== null) ? (plan.id || 'free') : (plan || 'free');

// Split three ways (2026-08-30 light-palette conversion) instead of one hex
// map with a '22'/'44' alpha-suffix string trick — that trick only works on
// literal hex, not on a var(--token) string, and --accent-fill is
// decorative-only (documented contrast rule: never carries text), so a
// single map can't correctly serve both the bar fill and the label/pill
// text at once the way the old one silently did.
const PLAN_BAR_COLORS   = { free: 'var(--muted)', lifetime_1tb: 'var(--accent-fill)', lifetime_2tb: 'var(--accent-fill)' }; // decorative fills only
const PLAN_LABEL_COLORS = { free: 'var(--muted)', lifetime_1tb: 'var(--accent)',      lifetime_2tb: 'var(--accent)' };      // text-safe
const PLAN_PILL_STYLE = {
  free:         { background: 'var(--surface-2)', color: 'var(--muted)', border: '1px solid var(--border)' },
  lifetime_1tb: { background: 'var(--green-dim)',  color: 'var(--accent)', border: '1px solid rgba(20,128,74,.3)' },
  lifetime_2tb: { background: 'var(--green-dim)',  color: 'var(--accent)', border: '1px solid rgba(20,128,74,.3)' },
};
const PLAN_EMOJIS = { free: '🆓', lifetime_1tb: '♾️', lifetime_2tb: '♾️' };

// Live tab "Mostrato" column (2026-08-30) — human labels for the values
// App.js's resolveShown() can send. Kept separate from the existing
// "Utente" loggato/anonimo pill rather than replacing it: that pill is
// still accurate (it IS the real isAuthenticated at that moment), this
// column adds what was missing — WHICH screen that state produced, so
// "anonimo" + "unlock" doesn't get misread as "anonymous visitor looking at
// the sign-up form" the way "anonimo" + path "/register" did.
const SHOWN_LABELS = {
  unlock: '🔒 sblocco password',
  twofa: '🔑 verifica 2FA',
  'auth-form': '📝 login/registrazione',
  'home-anonymous': '🏠 home pubblica',
  vault: '🔓 vault',
  admin: '⚙️ pannello admin',
  plans: '💳 piani',
  profile: '👤 profilo',
  'post-register': '🎉 dopo la registrazione',
  'payment-success': '✅ pagamento riuscito',
  'payment-cancel': '↩️ pagamento annullato',
  blog: '📰 blog', about: 'ℹ️ about', news: '📰 news',
  terms: '📄 termini', privacy: '📄 privacy', stacksocial: '🎟️ stacksocial',
};

// Live tab, colonna "Utente" e filtro "Escludi clienti paganti" (2026-09-01).
// Il valore arriva dal server (classifyViewer in routes/analytics.js), che lo
// ricava decodificando in modo facoltativo il token allegato al ping.
//
// null = riga scritta prima del 2026-09-01, oppure client vecchio che non
// manda ancora il token. Si mostra "loggato"/"anonimo" come si e' sempre
// fatto, e il filtro NON la nasconde mai: un valore mancante non e' la prova
// che quella visita fosse di un pagante.
const VIEWER_LABELS = {
  anon:  'anonimo',
  free:  '🔓 registrato',
  paid:  '💳 cliente pagante',
  admin: '⚙️ admin',
};
const VIEWER_STYLES = {
  anon:  { background:'var(--neutral-dim)', color:'var(--neutral)', border:'1px solid var(--neutral-border)' },
  free:  { background:'var(--green-dim)',   color:'var(--accent)',  border:'1px solid rgba(20,128,74,.3)' },
  paid:  { background:'var(--warn-dim)',    color:'var(--warn)',    border:'1px solid rgba(180,120,0,.3)' },
  admin: { background:'var(--neutral-dim)', color:'var(--text2)',   border:'1px solid var(--neutral-border)' },
};
// Chi viene nascosto quando l'interruttore e' attivo. Gli anonimi non ci sono
// mai dentro, per requisito: restano sempre visibili.
const VIEWER_HIDDEN_WHEN_FILTERED = new Set(['paid', 'admin']);
const SHOWN_STYLES = {
  unlock:   { background: 'var(--warn-dim)', color: 'var(--warn)', border: '1px solid var(--warn-border)' },
  twofa:    { background: 'var(--warn-dim)', color: 'var(--warn)', border: '1px solid var(--warn-border)' },
  'auth-form': { background: 'var(--neutral-dim)', color: 'var(--neutral)', border: '1px solid var(--neutral-border)' },
  vault:    { background: 'var(--green-dim)', color: 'var(--accent)', border: '1px solid rgba(20,128,74,.3)' },
  default:  { background: 'var(--surface-2)', color: 'var(--muted)', border: '1px solid var(--border)' },
};

// Payment `source`: undefined/'site' = a real checkout started from the site
// itself (Plans page, promo banner); anything else is a link clicked from an
// automated email — see backend/routes/{payments,checkoutClaim,promoClaim}.js.
const SOURCE_LABELS = {
  site: '🖱️ Sito',
  reminder: '📧 Reminder',
  nurture: '📧 Nurture',
  nurture_last_chance: '📧 Nurture (last chance)',
  home_promo_reminder: '📧 Home promo',
  referral_waiting_email: '📧 Referral',
};
function sourceLabel(source) {
  return SOURCE_LABELS[source] || (source ? `📧 ${source}` : '🖱️ Sito');
}

// EU-27 ISO country codes — for the Payments panel's EU/non-EU sales split
// (OSS one-stop-shop threshold monitoring: only EU cross-border B2C digital
// sales count toward it). `country` is the Stripe checkout billing-address
// country, only present on completed payments (see routes/webhookHandler.js).
const EU_COUNTRIES = new Set([
  'AT','BE','BG','HR','CY','CZ','DK','EE','FI','FR','DE','GR','HU','IE','IT',
  'LV','LT','LU','MT','NL','PL','PT','RO','SK','SI','ES','SE',
]);

// Allegati di un messaggio nel thread. L'anteprima delle immagini passa dallo
// stesso endpoint verificato del download: non esiste un URL diretto al file,
// quindi il blob si chiede con il token e si tiene come object URL finche' il
// pannello resta aperto.
function AllegatiMessaggio({ ticketId, attachments }) {
  const [anteprime, setAnteprime] = useState({});
  useEffect(() => {
    let vivo = true;
    const creati = [];
    (attachments || []).forEach((a) => {
      if (!a.mime?.startsWith('image/')) return;
      apiTicketAttachmentUrl(ticketId, a.id, { admin: true })
        .then((url) => {
          if (!vivo) { URL.revokeObjectURL(url); return; }
          creati.push(url);
          setAnteprime((p) => ({ ...p, [a.id]: url }));
        })
        .catch(() => {});
    });
    // Gli object URL vanno liberati: senza questo ogni apertura di un ticket
    // lascerebbe in memoria le immagini di quello precedente.
    return () => { vivo = false; creati.forEach((u) => URL.revokeObjectURL(u)); };
  }, [ticketId, attachments]);

  if (!attachments?.length) return null;
  const peso = (b) => (b < 1024 * 1024 ? `${Math.round(b / 1024)} KB` : `${(b / 1024 / 1024).toFixed(1)} MB`);
  return (
    <div className="ticket-att-admin">
      {attachments.map((a) => (
        <div key={a.id} className="ticket-att-admin-item">
          {anteprime[a.id] ? (
            <img src={anteprime[a.id]} alt={a.nome} className="ticket-att-thumb" />
          ) : (
            <div className="ticket-att-thumb ticket-att-thumb-doc">
              {a.mime === 'application/pdf' ? 'PDF' : '📎'}
            </div>
          )}
          <button type="button" className="ticket-att-dl"
                  onClick={() => apiTicketAttachment(ticketId, a.id, a.nome, { admin: true })}>
            {a.nome}
          </button>
          <span className="ticket-att-size">{peso(a.size)}</span>
        </div>
      ))}
    </div>
  );
}

// TB con una cifra decimale — o GB sotto il terabyte, perche' "0.0 TB" non
// dice niente. Solo per le tre cifre dello spazio qui sotto.
function tb(bytes) {
  if (bytes == null) return '—';
  const t = bytes / 1e12;
  if (t < 1) return `${(bytes / 1e9).toFixed(0)} GB`;
  return `${t.toFixed(1)} TB`;
}

export default function AdminPanel({ onBack, onProfile }) {
  const [tab, setTab] = useState('analytics');
  // Filtro della tab Live. Attivo di default: la domanda che si fa guardando
  // il Live e' "chi sta arrivando adesso", e le sessioni di chi ha gia'
  // comprato (e le proprie) sono rumore rispetto a quella domanda.
  //
  // E' un filtro di SOLA VISUALIZZAZIONE: l'API continua a restituire tutte
  // le visite, il server continua a registrarle tutte, e spegnere
  // l'interruttore le rimostra all'istante senza nessuna nuova chiamata.
  // Non tocca ne' Analytics ne' Accessi.
  const [liveExcludePaying, setLiveExcludePaying] = useState(() => {
    try { return localStorage.getItem('lc_live_excl_paying') !== '0'; } catch { return true; }
  });
  // Traffico interno (nostro): escluso di default, come i clienti paganti.
  // Anche questo e' un filtro di sola visualizzazione — il server marca le
  // righe con `interna`, non ne toglie nessuna.
  const [liveMostraInterno, setLiveMostraInterno] = useState(() => {
    try { return localStorage.getItem('lc_live_interno') === '1'; } catch { return false; }
  });
  const [quickStats, setQuickStats] = useState(null); // live visitors + today's visits (cheap, polled)
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [analyticsAgg, setAnalyticsAgg] = useState(null); // aggregated analytics (charts, top pages/countries/referrers)
  const [statsLoading, setStatsLoading] = useState(false);
  const statsLoadedAtRef = useRef(0);
  const [payments, setPayments] = useState([]);
  const [logins, setLogins] = useState([]);
  const [emails, setEmails] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  // FILTRO PER DATA DI REGISTRAZIONE nella scheda Users (2026-09-18).
  // 'oggi' e' la vista predefinita a ogni apertura della scheda; 'tutti' e'
  // l'elenco completo. "Oggi" vuol dire dalla mezzanotte italiana
  // (Europe/Rome), non UTC: la data si confronta come giorno del calendario
  // di Roma, cosi' all'una di notte si vedono i registrati dopo la mezzanotte
  // appena passata. La ricerca cerca SEMPRE su tutti: con una ricerca attiva
  // il filtro non si applica, altrimenti un cliente della settimana scorsa
  // sembrerebbe non esistere.
  const [filtroData, setFiltroData] = useState('oggi');
  useEffect(() => { if (tab === 'users') setFiltroData('oggi'); }, [tab]);
  const [loginSearch, setLoginSearch] = useState('');
  const [emailSearch, setEmailSearch] = useState('');
  // 2026-08-30 — "invii falliti" visibility: sendMail() already logs every
  // attempt (sent/skipped/failed) with the real error to email_log, this is
  // purely a filter on data that already exists, not new logging.
  const [emailFailedOnly, setEmailFailedOnly] = useState(false);

  // Ticket reply modal state
  const [ticketTarget, setTicketTarget] = useState(null); // full ticket, or {loading:true} while fetching
  const [ticketReply, setTicketReply]   = useState('');
  // NON spuntata di default (2026-09-06). Era true, quindi ogni risposta
  // chiudeva il ticket a meno che qualcuno non si ricordasse di togliere la
  // spunta: un cliente si e' visto rispondere "✅ Resolved" su un problema
  // ancora aperto. Chiudere un ticket dev'essere una scelta, non quello che
  // succede se non stai attento.
  const [ticketClose, setTicketClose]   = useState(false);
  const [ticketBusy, setTicketBusy]     = useState(false);

  // Change-plan modal state (was "gift") — two distinct actions live in the
  // Users tab now: this one (manual change, never touches code inventory)
  // and Apply StackSocial Code below (consumes one code, never asks why).
  const [giftTarget, setGiftTarget] = useState(null);
  const [giftPlan, setGiftPlan] = useState('lifetime_1tb');
  const [giftReason, setGiftReason] = useState('');
  const [giftLoading, setGiftLoading] = useState(false);
  const [giftBlocked, setGiftBlocked] = useState(null); // { message, usedBytes, limitBytes } | null

  // Apply-StackSocial-code modal state — separate from the above on purpose
  // (see the header comment on handleApplyCode below).
  const [applyCodeTarget, setApplyCodeTarget] = useState(null); // user
  const [applyCodeValue, setApplyCodeValue] = useState('');
  const [applyCodeLoading, setApplyCodeLoading] = useState(false);
  const [applyCodeError, setApplyCodeError] = useState('');

  // StackSocial tab state
  const [ssCodes, setSsCodes] = useState(null);
  // ── Posti (2026-09-07) ──
  const [posti, setPosti] = useState(null);
  const [postiInput, setPostiInput] = useState({});
  const [postiMsg, setPostiMsg] = useState('');
  const [postiBusy, setPostiBusy] = useState(false);
  const [ssTrend, setSsTrend] = useState(null);
  const [ssLookupInput, setSsLookupInput] = useState('');
  const [ssLookupResult, setSsLookupResult] = useState(null); // {…} | 'not_found' | null
  const [ssLookupLoading, setSsLookupLoading] = useState(false);

  const loadAll = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const [u, p, l, m, t] = await Promise.all([apiAdminUsers(), apiAdminPayments(), apiAdminLogins(500), apiAdminEmails(500), apiAdminTickets()]);
      setUsers(u); setPayments(p); setLogins(l); setEmails(m); setTickets(t);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, []);

  // Quick stats (live visitors + today's visits) — cheap, safe to poll every 30s
  useEffect(() => {
    const fetchQuickStats = () => apiGetQuickStats().then(qs => { if (qs) setQuickStats(qs); }).catch(()=>{});
    fetchQuickStats();
    const t = setInterval(fetchQuickStats, 30000);
    return () => clearInterval(t);
  }, []);

  // In-flight upload count + maintenance-mode state — shown as a persistent
  // bar (not tab-gated) since this is exactly what you'd check right before
  // or during a deploy. Polled every 10s, same cadence as the Live tab.
  const [uploadStatus, setUploadStatus] = useState(null);
  const [maintenance, setMaintenance] = useState(null);
  const [maintenanceBusy, setMaintenanceBusy] = useState(false);
  useEffect(() => {
    const load = () => {
      apiAdminUploadStatus().then(setUploadStatus).catch(()=>{});
      apiAdminGetMaintenance().then(m => setMaintenance(m.enabled)).catch(()=>{});
    };
    load();
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
  }, []);

  async function handleToggleMaintenance() {
    const next = !maintenance;
    if (next && !window.confirm('Attivare la modalità manutenzione? I nuovi upload verranno rifiutati (quelli in corso continuano normalmente).')) return;
    setMaintenanceBusy(true);
    try {
      const res = await apiAdminSetMaintenance(next);
      setMaintenance(res.enabled);
    } catch (e) { alert(e.message); }
    finally { setMaintenanceBusy(false); }
  }

  useEffect(() => { loadAll(); }, [loadAll]);

  // Stats tab — heavy aggregated calcs, loaded lazily on click, cached 60s
  const loadStatsTab = useCallback(async (force = false) => {
    if (!force && Date.now() - statsLoadedAtRef.current < 60000) return;
    setStatsLoading(true);
    try {
      const [s, a] = await Promise.all([apiAdminStats(), apiGetAnalytics()]);
      setStats(s); setAnalyticsAgg(a);
      statsLoadedAtRef.current = Date.now();
    } catch (e) { setError(e.message); }
    finally { setStatsLoading(false); }
  }, []);

  useEffect(() => { if (tab === 'stats') loadStatsTab(); }, [tab, loadStatsTab]);

  // Live tab — polls only while it's the open tab, every 10s, stops the
  // instant you switch away (interval cleared in the effect cleanup).
  const [liveVisits, setLiveVisits] = useState([]);
  // Lista effettivamente mostrata dalla tab Live. Derivata, non uno stato:
  // liveVisits resta sempre la risposta integrale del server, cosi' togliere
  // la spunta rimostra tutto senza aspettare il prossimo poll da 10s.
  const liveVisitsShown = useMemo(() => {
    let r = liveVisits;
    if (liveExcludePaying) r = r.filter(v => !VIEWER_HIDDEN_WHEN_FILTERED.has(v.viewer));
    if (!liveMostraInterno) r = r.filter(v => !v.interna);
    return r;
  }, [liveVisits, liveExcludePaying, liveMostraInterno]);
  const liveHiddenCount = liveVisits.length - liveVisitsShown.length;
  const liveInterneCount = liveVisits.filter(v => v.interna).length;
  // Data di stacco del filtro sul traffico interno, presa dal server
  // (quick-stats -> utils/visitaInterna.js): non riscritta a mano qui.
  const filtroDal = quickStats?.filtroInternoDal || null;
  useEffect(() => {
    if (tab !== 'live') return;
    let cancelled = false;
    const load = () => apiAdminLiveVisits().then(v => { if (!cancelled) setLiveVisits(v); }).catch(() => {});
    load();
    const interval = setInterval(load, 10000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [tab]);

  // "Cambia piano manualmente" — support/gift. Deliberately separate from
  // handleApplyCode below: this one never touches stacksocial_codes, that
  // one only ever consumes a code and never asks for a reason. Merging them
  // would make it impossible to trust the code-inventory counts.
  async function handleGift() {
    if (!giftReason.trim()) { alert('Il motivo è obbligatorio.'); return; }
    setGiftLoading(true);
    setGiftBlocked(null);
    try {
      const result = await apiAdminChangePlan(giftTarget.id, giftPlan, giftReason.trim());
      if (result.blocked) {
        setGiftBlocked(result);
        return; // modal stays open — nothing changed, show why
      }
      setGiftTarget(null);
      setGiftReason('');
      await loadAll();
    } catch (e) { alert(e.message); }
    finally { setGiftLoading(false); }
  }

  // "Applica un codice StackSocial a un utente esistente" — for someone who
  // bought on StackSocial but already had an account here. Consumes real
  // inventory (same atomic transaction as the public redemption page), so
  // this stays a single explicit action with no reason field and no way to
  // combine it with the manual change above.
  async function handleApplyCode() {
    const code = applyCodeValue.trim().toLowerCase().replace(/[\s-]/g, '');
    if (!code) return;
    setApplyCodeLoading(true);
    setApplyCodeError('');
    try {
      await apiAdminStackSocialApply(code, applyCodeTarget.id);
      setApplyCodeTarget(null);
      setApplyCodeValue('');
      await loadAll();
      if (tab === 'stacksocial') await loadStackSocialTab();
    } catch (e) { setApplyCodeError(e.message); }
    finally { setApplyCodeLoading(false); }
  }

  const loadStackSocialTab = useCallback(async () => {
    try {
      const [codes, trend] = await Promise.all([apiAdminStackSocialCodes(), apiAdminStackSocialTrend()]);
      setSsCodes(codes);
      setSsTrend(trend);
    } catch (e) { console.error('StackSocial tab load failed:', e.message); }
  }, []);

  useEffect(() => { if (tab === 'stacksocial') loadStackSocialTab(); }, [tab, loadStackSocialTab]);

  const caricaPosti = useCallback(async () => {
    try { setPosti(await apiAdminPosti()); } catch (e) { console.error('posti:', e.message); }
  }, []);
  useEffect(() => { if (tab === 'posti') caricaPosti(); }, [tab, caricaPosti]);

  // Le due operazioni sono diverse di proposito: "aggiungi" somma ai rimasti
  // (il gesto di tutti i giorni), "imposta" fissa i rimasti a un numero
  // preciso (il gesto di quando si riparte). Dopo ognuna si ricarica dal
  // server invece di aggiornare a mano lo stato locale: cosi' quello che si
  // vede e' quello che il sito mostrera' ai clienti, non una previsione.
  async function agisciSuiPosti(plan, azione) {
    const v = Number(postiInput[plan]);
    if (!Number.isFinite(v)) { setPostiMsg('Scrivi un numero.'); return; }
    setPostiBusy(true); setPostiMsg('');
    try {
      const r = azione === 'aggiungi' ? await apiAdminPostiAggiungi(plan, v) : await apiAdminPostiImposta(plan, v);
      setPostiMsg(`${plan}: ora ${r.piano.rimasti} posti rimasti.`);
      setPostiInput(x => ({ ...x, [plan]: '' }));
      await caricaPosti();
    } catch (e) { setPostiMsg(e.message); }
    finally { setPostiBusy(false); }
  }

  async function handleCodeLookup() {
    const code = ssLookupInput.trim().toLowerCase().replace(/[\s-]/g, '');
    if (!code) return;
    setSsLookupLoading(true);
    setSsLookupResult(null);
    try {
      const row = await apiAdminStackSocialLookup(code);
      setSsLookupResult(row);
    } catch (e) {
      setSsLookupResult('not_found');
    } finally { setSsLookupLoading(false); }
  }

  async function handleBan(user) {
    if (!window.confirm(`${user.banned ? 'Unban' : 'Ban'} @${user.username}?`)) return;
    try {
      user.banned ? await apiAdminUnban(user.id) : await apiAdminBan(user.id);
      await loadAll();
    } catch (e) { alert(e.message); }
  }

  async function handleDelete(user) {
    if (!window.confirm(`Delete @${user.username} and ALL their files? This is irreversible!`)) return;
    try {
      await apiAdminDeleteUser(user.id);
      await loadAll();
    } catch (e) { alert(e.message); }
  }

  async function handleReminder(user) {
    if (!user.email) { alert('Questo utente non ha un indirizzo email.'); return; }
    if (!window.confirm(`Inviare email di reminder pagamento a @${user.username} (${user.email})?`)) return;
    try {
      const r = await apiAdminPaymentReminder(user.id);
      alert(`✉️ Reminder inviato a ${r.sentTo}`);
      await loadAll();
    } catch (e) { alert(e.message); }
  }

  async function handleRole(user, role) {
    if (!window.confirm(`Make @${user.username} ${role}?`)) return;
    try {
      await apiAdminSetRole(user.id, role);
      await loadAll();
    } catch (e) { alert(e.message); }
  }

  async function openTicket(id) {
    setTicketTarget({ loading: true, id });
    // Riazzerata a false a ogni apertura: non deve restare spuntata da un
    // ticket all'altro (vedi il commento sullo stato).
    setTicketReply(''); setTicketClose(false);
    try { setTicketTarget(await apiAdminTicketGet(id)); }
    catch (e) { setTicketTarget(null); alert(e.message); }
  }

  async function handleTicketReply() {
    if (!ticketReply.trim() || !ticketTarget) return;
    setTicketBusy(true);
    try {
      const { ticket } = await apiAdminTicketReply(ticketTarget.id, ticketReply.trim(), ticketClose ? 'closed' : 'answered');
      setTicketTarget(ticket);
      setTicketReply('');
      await loadAll();
    } catch (e) { alert(e.message); }
    finally { setTicketBusy(false); }
  }

  async function handleTicketStatusChange(status) {
    if (!ticketTarget || status === ticketTarget.status) return;
    setTicketBusy(true);
    try {
      const { ticket } = await apiAdminTicketStatus(ticketTarget.id, status);
      setTicketTarget(ticket);
      await loadAll();
    } catch (e) { alert(e.message); }
    finally { setTicketBusy(false); }
  }

  const giornoRoma = (d) => {
    try { return new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Rome', year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date(d)); }
    catch { return ''; }
  };
  const oggiRoma = giornoRoma(new Date());
  const usersOggi = users.filter(u => giornoRoma(u.createdAt) === oggiRoma);
  const ricercaAttiva = search.trim() !== '';
  const baseUtenti = ricercaAttiva ? users : (filtroData === 'oggi' ? usersOggi : users);
  const filteredUsers = baseUtenti
    .filter(u => u.username.toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

  return (
    <div className="admin-page">
      {/* Change-Plan Modal (manual — support/gift, never touches code inventory) */}
      {giftTarget && (
        <div className="modal-overlay" onClick={() => { setGiftTarget(null); setGiftBlocked(null); setGiftReason(''); }}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h3>🎁 Cambia piano — @{giftTarget.username}</h3>
            <div className="modal-field">
              <label>Piano</label>
              <select value={giftPlan} onChange={e => { setGiftPlan(e.target.value); setGiftBlocked(null); }}>
                <option value="lifetime_1tb">♾️ 1 TB Lifetime</option>
                <option value="lifetime_2tb">♾️ 2 TB Lifetime</option>
                <option value="free">🆓 Gratuito (downgrade)</option>
              </select>
            </div>
            <div className="modal-field">
              <label>Motivo (obbligatorio)</label>
              <textarea
                rows={2}
                value={giftReason}
                onChange={e => { setGiftReason(e.target.value); setGiftBlocked(null); }}
                placeholder="Es: rimborso parziale concordato via ticket #142"
              />
            </div>
            {giftBlocked && (
              <div className="admin-blocked-note">
                ⛔ {giftBlocked.message}
              </div>
            )}
            <div className="modal-actions">
              <button className="btn-cancel" onClick={() => { setGiftTarget(null); setGiftBlocked(null); setGiftReason(''); }}>Annulla</button>
              <button className="btn-gift" onClick={handleGift} disabled={giftLoading || !giftReason.trim()}>
                {giftLoading ? 'Salvataggio…' : '🎁 Applica'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Apply StackSocial Code Modal — separate action, consumes inventory */}
      {applyCodeTarget && (
        <div className="modal-overlay" onClick={() => { setApplyCodeTarget(null); setApplyCodeError(''); setApplyCodeValue(''); }}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h3>🎟️ Applica codice StackSocial — @{applyCodeTarget.username}</h3>
            <p className="admin-modal-hint">Per chi ha comprato su StackSocial ma aveva già un account qui. Il codice viene consumato e il piano assegnato nella stessa transazione atomica del percorso pubblico.</p>
            <div className="modal-field">
              <label>Codice</label>
              <input
                type="text"
                value={applyCodeValue}
                onChange={e => { setApplyCodeValue(e.target.value); setApplyCodeError(''); }}
                placeholder="Incolla qui il codice inviato dal cliente"
                autoFocus
              />
            </div>
            {applyCodeError && <div className="admin-blocked-note">⛔ {applyCodeError}</div>}
            <div className="modal-actions">
              <button className="btn-cancel" onClick={() => { setApplyCodeTarget(null); setApplyCodeError(''); setApplyCodeValue(''); }}>Annulla</button>
              <button className="btn-gift" onClick={handleApplyCode} disabled={applyCodeLoading || !applyCodeValue.trim()}>
                {applyCodeLoading ? 'Applicazione…' : '🎟️ Applica codice'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="admin-header">
        <div className="admin-header-left">
          <div className="admin-logo">⚙️ <strong>LifetimeCloud Admin</strong></div>
        </div>
        <div className="admin-header-right">
          {onProfile && <button className="btn-back" onClick={onProfile}>👤 Profilo</button>}
          <button className="admin-logout-btn" onClick={() => { localStorage.clear(); sessionStorage.clear(); window.location.reload(); }}>↪ Sign out</button>
        </div>
        <button className="btn-refresh" onClick={loadAll}>↻ Refresh</button>
      </div>

      {error && <div className="error-banner">⚠ {error}</div>}

      {/* Deploy status bar — upload counter + maintenance toggle, always visible */}
      <div className="admin-toolbar" style={{gap:16, alignItems:'center', flexWrap:'wrap'}}>
        <span style={{fontSize:14}}>
          📤 Upload in corso: <strong>{uploadStatus ? uploadStatus.count : '…'}</strong>
        </span>
        <button
          onClick={handleToggleMaintenance}
          disabled={maintenanceBusy || maintenance === null}
          style={{
            padding:'6px 14px', borderRadius:999, border:'none', cursor:'pointer', fontWeight:700, fontSize:13,
            background: maintenance ? 'var(--danger)' : 'var(--accent)', color:'var(--accent-ink)', opacity: maintenanceBusy ? 0.6 : 1
          }}
        >
          {maintenance ? '🔧 Manutenzione: ON (upload bloccati)' : '✅ Manutenzione: OFF'}
        </button>
        {uploadStatus?.count > 0 && (
          <div style={{width:'100%', overflowX:'auto'}}>
            <table className="users-table" style={{marginTop:8}}>
              <thead><tr><th>Utente</th><th>Sorgente</th><th>Dimensione</th><th>Da quanto</th></tr></thead>
              <tbody>
                {uploadStatus.uploads.map(u => (
                  <tr key={u.id}>
                    <td>{u.email || u.userId}</td>
                    <td>{u.source}</td>
                    <td>{formatBytes(u.sizeBytes)}</td>
                    <td>{u.elapsedSeconds}s</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="admin-tabs">
        {[['analytics','📈 Analytics'],['stats','📊 Stats'],['live','🔴 Live'],['users','👥 Users'],['logins','🕒 Accessi'],['emails','✉️ Emails'],['payments','💳 Payments'],['posti','🪑 Posti'],['stacksocial','🎟️ StackSocial'],['tickets',`🎫 Tickets${tickets.filter(t=>t.status==='open').length ? ` (${tickets.filter(t=>t.status==='open').length})` : ''}`]].map(([id,label]) => (
          <button key={id} className={tab === id ? 'active' : ''} onClick={() => setTab(id)}>{label}</button>
        ))}
      </div>

      {loading ? (
        <div className="admin-loading">Loading…</div>
      ) : (
        <>
          {/* ── USERS TAB ── */}
          {tab === 'users' && (
            <div className="admin-section">
              <div className="admin-toolbar admin-toolbar-utenti">
                <div className={`admin-filtro-data${ricercaAttiva ? ' sospeso' : ''}`} role="group" aria-label="Registrati">
                  <button type="button" className={filtroData === 'oggi' ? 'active' : ''} aria-pressed={filtroData === 'oggi'} onClick={() => setFiltroData('oggi')}>Oggi ({usersOggi.length})</button>
                  <span className="admin-filtro-sep">·</span>
                  <button type="button" className={filtroData === 'tutti' ? 'active' : ''} aria-pressed={filtroData === 'tutti'} onClick={() => setFiltroData('tutti')}>Tutti ({users.length})</button>
                </div>
                <input
                  className="admin-search"
                  placeholder="🔍 Search username…"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                />
                <span className="user-count">{filteredUsers.length} users{ricercaAttiva ? ' · ricerca su tutti' : ''}</span>
              </div>

              <div className="users-table-wrap">
                <table className="users-table">
                  <thead>
                    <tr>
                      <th>User</th><th>Email</th>
                      <th>Source</th>
                      <th>Landing page</th>
                      <th>Plan</th>
                      <th>Files</th>
                      <th>Storage</th>
                      <th>Role</th>
                      <th>Status</th>
                      <th>Joined</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUsers.map((user, i) => (
                      <tr key={user.id} className={[i % 2 === 1 && 'admin-row-even', user.banned && 'banned-row'].filter(Boolean).join(' ')}>
                        <td className="td-user">
                          <span className="td-username">@{user.username}</span>
                          {user.role === 'admin' && <span className="badge-admin">admin</span>}
                        </td>
                        <td style={{fontSize:11,color:'var(--muted)',fontFamily:'JetBrains Mono,monospace'}}>{user.email||'—'}</td>
                        <td style={{fontSize:11,color:'var(--muted)',fontFamily:'JetBrains Mono,monospace'}}>
                          {user.source || 'direct'}
                          {user.campaign && <span style={{display:'block',color:'var(--muted)',fontSize:10}}>{user.campaign}</span>}
                        </td>
                        <td style={{fontSize:11,color:'var(--muted)',fontFamily:'JetBrains Mono,monospace',maxWidth:140}} title={user.landing_page || ''}>
                          {user.landing_page
                            ? (user.landing_page.length > 24 ? user.landing_page.slice(0, 21) + '…' : user.landing_page)
                            : '—'}
                        </td>
                        <td>
                          <span className="plan-pill" style={PLAN_PILL_STYLE[getPlanId(user.plan)] || PLAN_PILL_STYLE.free}>
                            {PLAN_EMOJIS[getPlanId(user.plan)]} {getPlanId(user.plan)}
                          </span>
                          {user.activePlans && user.activePlans.length > 1 && (
                            <span className="plan-exp" style={{color:'var(--accent)'}}>+{user.activePlans.length} piani</span>
                          )}
                          {user.planExpiresAt && (
                            <span className="plan-exp">exp {new Date(user.planExpiresAt).toLocaleDateString('it-IT')}</span>
                          )}
                        </td>
                        <td className="td-num">{user.fileCount}</td>
                        <td className="td-num">{formatBytes(user.storageUsed)}</td>
                        <td>
                          <select
                            value={user.role}
                            onChange={e => handleRole(user, e.target.value)}
                            className="role-select"
                            disabled={user.role === 'admin' && users.filter(u => u.role === 'admin').length === 1}
                          >
                            <option value="user">user</option>
                            <option value="admin">admin</option>
                          </select>
                        </td>
                        <td>
                          <span className={`status-dot ${user.banned ? 'banned' : 'active'}`}>
                            {user.banned ? '🔴 banned' : '🟢 active'}
                          </span>
                        </td>
                        <td className="td-date">
                          {new Date(user.createdAt).toLocaleDateString('it-IT')}
                          <span style={{display:'block',color:'var(--muted)',fontSize:10}}>{new Date(user.createdAt).toLocaleTimeString('it-IT', {hour:'2-digit',minute:'2-digit'})}</span>
                        </td>
                        <td className="td-actions">
                          <button className="btn-action gift" onClick={() => { setGiftTarget(user); setGiftPlan('lifetime_1tb'); setGiftReason(''); setGiftBlocked(null); }} title="Cambia piano manualmente">🎁</button>
                          <button className="btn-action gift" onClick={() => { setApplyCodeTarget(user); setApplyCodeValue(''); setApplyCodeError(''); }} title="Applica codice StackSocial">🎟️</button>
                          <button
                            className="btn-action remind"
                            onClick={() => handleReminder(user)}
                            title={!user.email ? 'No email' : getPlanId(user.plan) !== 'free' ? 'Already paid' : (user.lastPaymentReminderAt ? `Last sent: ${new Date(user.lastPaymentReminderAt).toLocaleString('it-IT')}` : 'Send payment reminder')}
                            disabled={!user.email || getPlanId(user.plan) !== 'free'}
                          >💌</button>
                          <button className="btn-action ban" onClick={() => handleBan(user)} title={user.banned ? 'Unban' : 'Ban'}>
                            {user.banned ? '✅' : '🚫'}
                          </button>
                          <button className="btn-action del" onClick={() => handleDelete(user)} title="Delete user" disabled={user.role === 'admin'}>🗑️</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}


          {/* ── LOGINS TAB ── */}
          {tab === 'logins' && (
            <div className="admin-section">
              <div className="admin-toolbar">
                <input
                  className="admin-search"
                  placeholder="🔍 Cerca username o IP…"
                  value={loginSearch}
                  onChange={e => setLoginSearch(e.target.value)}
                />
                <span className="user-count">
                  {logins.filter(l => {
                    const q = loginSearch.toLowerCase();
                    return !q || (l.username||'').toLowerCase().includes(q) || (l.ip||'').toLowerCase().includes(q);
                  }).length} accessi
                </span>
              </div>

              <div className="users-table-wrap">
                <table className="users-table">
                  <thead>
                    <tr>
                      <th>Data / Ora</th>
                      <th>Utente</th>
                      <th>Evento</th>
                      <th>IP</th>
                      <th>Browser</th>
                    </tr>
                  </thead>
                  <tbody>
                    {logins
                      .filter(l => {
                        const q = loginSearch.toLowerCase();
                        return !q || (l.username||'').toLowerCase().includes(q) || (l.ip||'').toLowerCase().includes(q);
                      })
                      .map((l, i) => {
                        const evt = l.event || 'login';
                        // Was a single hex color re-used via a '22'/'44' alpha-suffix
                        // string trick — doesn't work once the color is a var(--token)
                        // string, so each state gets its own explicit pill style instead
                        // (register=preview purple, login_2fa=accent green, plain
                        // login=neutral grey — no blue, per the admin light conversion).
                        const evtStyle = evt === 'register'
                          ? { background: 'var(--preview-dim)', color: 'var(--preview)', border: '1px solid var(--preview-border)' }
                          : evt === 'login_2fa'
                          ? { background: 'var(--green-dim)', color: 'var(--accent)', border: '1px solid rgba(20,128,74,.3)' }
                          : { background: 'var(--neutral-dim)', color: 'var(--neutral)', border: '1px solid var(--neutral-border)' };
                        const evtLabel = evt === 'register' ? '🆕 register' : evt === 'login_2fa' ? '🔐 login + 2FA' : '🔓 login';
                        const ua = l.userAgent || '';
                        const uaShort = ua.length > 60 ? ua.slice(0, 60) + '…' : ua;
                        return (
                          <tr key={l.id} className={i % 2 === 1 ? 'admin-row-even' : ''}>
                            <td className="td-date">{new Date(l.at).toLocaleString('it-IT')}</td>
                            <td>@{l.username}</td>
                            <td>
                              <span className="plan-pill" style={evtStyle}>
                                {evtLabel}
                              </span>
                            </td>
                            <td style={{fontFamily:'JetBrains Mono,monospace',fontSize:12,color:'var(--text2)'}}>{l.ip || '—'}</td>
                            <td title={ua} style={{fontFamily:'JetBrains Mono,monospace',fontSize:11,color:'var(--text3)',maxWidth:320,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{uaShort || '—'}</td>
                          </tr>
                        );
                      })}
                    {logins.length === 0 && (
                      <tr><td colSpan="5" style={{textAlign:'center',color:'var(--muted)',padding:'32px'}}>Nessun accesso registrato</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* ── LIVE TAB — last 100 lines of backend/logs/visits.log, tail-read on
               each poll (every 10s, only while this tab is open — see the effect
               above). Known bots/scanners are already excluded server-side
               (utils/liveVisits.js); rows still shown here can be flagged
               `suspicious` (no referrer, repeated hits from the same IP within
               5s) for visits that pass the UA filter but still look scripted. */}
          {tab === 'live' && (
            <div className="admin-section">
              <div className="admin-toolbar">
                <span className="user-count">
                  {liveVisitsShown.length} visite recenti · aggiornamento ogni 10s · bot noti già esclusi
                  {liveExcludePaying && liveHiddenCount > 0 && ` · ${liveHiddenCount} nascoste dal filtro`}
                </span>
                <label
                  style={{ marginLeft:'auto', display:'flex', alignItems:'center', gap:8, cursor:'pointer', fontSize:13, color:'var(--text2)' }}
                  title="Nasconde le sessioni di utenti autenticati con un piano a pagamento attivo e degli account admin. Solo visualizzazione: i dati continuano a essere registrati per intero e le sessioni anonime restano sempre visibili. Non tocca Analytics né Accessi."
                >
                  <input
                    type="checkbox"
                    checked={liveExcludePaying}
                    onChange={(e) => {
                      const v = e.target.checked;
                      setLiveExcludePaying(v);
                      try { localStorage.setItem('lc_live_excl_paying', v ? '1' : '0'); } catch {}
                    }}
                  />
                  Escludi clienti paganti
                </label>
                <label
                  style={{ display:'flex', alignItems:'center', gap:8, cursor:'pointer', fontSize:13, color:'var(--text2)' }}
                  title="Traffico generato da noi: richieste dal server stesso, reti private, strumenti a riga di comando e browser di automazione, e i percorsi delle anteprime. Escluso di default dai conteggi. Le righe non vengono mai cancellate dal log."
                >
                  <input
                    type="checkbox"
                    checked={liveMostraInterno}
                    onChange={(e) => {
                      const v = e.target.checked;
                      setLiveMostraInterno(v);
                      try { localStorage.setItem('lc_live_interno', v ? '1' : '0'); } catch {}
                    }}
                  />
                  Mostra traffico interno{liveInterneCount > 0 ? ` (${liveInterneCount})` : ''}
                </label>
              </div>
              <div className="users-table-wrap">
                <table className="users-table">
                  <thead>
                    <tr>
                      <th>Ora</th>
                      <th>Path</th>
                      <th>Referrer</th>
                      <th>Paese</th>
                      <th>Utente</th>
                      <th title="Cosa ha mostrato davvero il router lato client — non dedotto dal path, letto da resolveShown() in App.js">Mostrato</th>
                      <th>User-Agent</th>
                    </tr>
                  </thead>
                  <tbody>
                    {liveVisitsShown.map((v, i) => {
                      const ua = v.userAgent || '';
                      const uaShort = ua.length > 44 ? ua.slice(0, 44) + '…' : ua;
                      return (
                        <tr key={i} className={i % 2 === 1 ? 'admin-row-even' : ''} style={v.suspicious ? { background: 'var(--warn-dim)' } : undefined}>
                          <td className="td-date">{new Date(v.time).toLocaleTimeString('it-IT')}</td>
                          <td style={{fontFamily:'JetBrains Mono,monospace',fontSize:12,color:'var(--text2)'}}>
                            {v.suspicious && <span title="Nessun referrer, più hit dallo stesso IP in pochi secondi — probabile scanner/bot con UA browser-like">⚠️ </span>}
                            {v.path}
                          </td>
                          <td style={{fontFamily:'JetBrains Mono,monospace',fontSize:12,color:'var(--text3)'}}>{v.referrer || '—'}</td>
                          <td style={{fontFamily:'JetBrains Mono,monospace',fontSize:12,color:'var(--text3)'}}>{v.country || '—'}</td>
                          <td>
                            {/* viewer arriva dal server (classifyViewer). Sulle righe
                                precedenti al 2026-09-01 e' null: si ricade sul vecchio
                                loggato/anonimo, che resta accurato per quel che sa. */}
                            {v.interna
                              ? <span className="plan-pill" style={{ background:'var(--bg3)', color:'var(--text3)', border:'1px solid var(--border)' }}>interno</span>
                              : v.viewer
                              ? <span className="plan-pill" style={VIEWER_STYLES[v.viewer] || VIEWER_STYLES.anon}>{VIEWER_LABELS[v.viewer] || v.viewer}</span>
                              : v.loggedIn
                                ? <span className="plan-pill" style={{ background:'var(--green-dim)', color:'var(--accent)', border:'1px solid rgba(20,128,74,.3)' }}>🔓 loggato</span>
                                : <span className="plan-pill" style={{ background:'var(--neutral-dim)', color:'var(--neutral)', border:'1px solid var(--neutral-border)' }}>anonimo</span>}
                          </td>
                          <td>
                            {/* Real component identity, sent by the client's own resolveShown()
                                — see App.js. null on log lines from before this fix (older
                                clients never sent it): shown as "—", never guessed from path,
                                same "admit what we don't know" rule as everywhere else tonight. */}
                            {v.shown
                              ? <span className="plan-pill" style={SHOWN_STYLES[v.shown] || SHOWN_STYLES.default}>{SHOWN_LABELS[v.shown] || v.shown}</span>
                              : <span style={{color:'var(--muted)',fontSize:12}}>—</span>}
                          </td>
                          <td title={ua} style={{fontFamily:'JetBrains Mono,monospace',fontSize:11,color:'var(--text3)',maxWidth:280,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{uaShort || '—'}</td>
                        </tr>
                      );
                    })}
                    {liveVisitsShown.length === 0 && (
                      <tr><td colSpan="7" style={{textAlign:'center',color:'var(--muted)',padding:'32px'}}>
                        {liveVisits.length === 0
                          ? 'Nessuna visita registrata ancora'
                          : `Tutte le ${liveVisits.length} visite recenti sono nascoste dal filtro — togli la spunta per vederle`}
                      </td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* ── EMAILS TAB ── */}
          {tab === 'emails' && (
            <div className="admin-section">
              <div className="admin-toolbar">
                <input
                  className="admin-search"
                  placeholder="🔍 Cerca destinatario o oggetto…"
                  value={emailSearch}
                  onChange={e => setEmailSearch(e.target.value)}
                />
                <label style={{display:'flex',alignItems:'center',gap:6,fontSize:13,color:'var(--text2)',cursor:'pointer'}}>
                  <input type="checkbox" checked={emailFailedOnly} onChange={e => setEmailFailedOnly(e.target.checked)} />
                  Solo falliti
                </label>
                <span className="user-count">
                  {emails.filter(m => {
                    const q = emailSearch.toLowerCase();
                    const matchesSearch = !q || (m.to||'').toLowerCase().includes(q) || (m.subject||'').toLowerCase().includes(q);
                    const matchesFailed = !emailFailedOnly || m.status === 'failed';
                    return matchesSearch && matchesFailed;
                  }).length} email
                </span>
              </div>

              <div className="users-table-wrap">
                <table className="users-table">
                  <thead>
                    <tr>
                      <th>Data / Ora</th>
                      <th>Destinatario</th>
                      <th>Oggetto</th>
                      <th>Stato</th>
                      <th>Motivo</th>
                    </tr>
                  </thead>
                  <tbody>
                    {emails
                      .filter(m => {
                        const q = emailSearch.toLowerCase();
                        const matchesSearch = !q || (m.to||'').toLowerCase().includes(q) || (m.subject||'').toLowerCase().includes(q);
                        const matchesFailed = !emailFailedOnly || m.status === 'failed';
                        return matchesSearch && matchesFailed;
                      })
                      .map((m, i) => {
                        const statusStyle = m.status === 'sent'
                          ? { background: 'var(--green-dim)', color: 'var(--accent)', border: '1px solid rgba(20,128,74,.3)' }
                          : m.status === 'skipped'
                          ? { background: 'var(--neutral-dim)', color: 'var(--neutral)', border: '1px solid var(--neutral-border)' }
                          : { background: 'var(--danger-dim)', color: 'var(--danger)', border: '1px solid var(--danger-border)' };
                        const statusLabel = m.status === 'sent' ? '✅ inviata' : m.status === 'skipped' ? '⏭ skip' : '❌ errore';
                        return (
                          <tr key={m.id} className={i % 2 === 1 ? 'admin-row-even' : ''}>
                            <td className="td-date">{new Date(m.at).toLocaleString('it-IT')}</td>
                            <td style={{fontFamily:'JetBrains Mono,monospace',fontSize:12,color:'var(--text2)'}}>{m.to}</td>
                            <td style={{maxWidth:280,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{m.subject}</td>
                            <td>
                              <span className="plan-pill" style={statusStyle}>
                                {statusLabel}
                              </span>
                            </td>
                            {/* Real error text, not a hover-only tooltip — "voglio vedere il
                                motivo", not have to guess from a status pill alone. */}
                            <td style={{fontSize:12,color:'var(--danger)',maxWidth:320,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}} title={m.error||''}>
                              {m.error || (m.status === 'failed' ? '(nessun dettaglio salvato)' : '—')}
                            </td>
                          </tr>
                        );
                      })}
                    {emails.length === 0 && (
                      <tr><td colSpan="5" style={{textAlign:'center',color:'var(--muted)',padding:'32px'}}>Nessuna email registrata</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* ── ANALYTICS TAB ── */}
          {tab === 'analytics' && (
            <div>
              {!quickStats ? (
                <div style={{color:'var(--text3)',padding:40,textAlign:'center'}}>Loading analytics…</div>
              ) : (
                <>
                  {/* Live visitors */}
                  <div style={{background:'var(--green-dim)',border:'1px solid rgba(20,128,74,.3)',borderRadius:12,padding:'14px 20px',marginBottom:20,display:'flex',alignItems:'center',gap:14,flexWrap:'wrap'}}>
                    <div style={{width:10,height:10,borderRadius:'50%',background:'var(--accent)',boxShadow:'0 0 8px var(--accent)'}}/>
                    <span style={{fontSize:22,fontWeight:900,color:'var(--accent)',fontFamily:'JetBrains Mono,monospace'}}>{quickStats.liveVisitors || 0}</span>
                    <span style={{fontSize:14,color:'var(--text2)'}}>visitor{quickStats.liveVisitors !== 1 ? 's' : ''} online right now</span>
                    {quickStats.liveDetails?.map((v,i) => (
                      <span key={i} style={{fontSize:11,background:'var(--bg)',border:'1px solid var(--border)',padding:'2px 8px',borderRadius:99,fontFamily:'JetBrains Mono,monospace',color:'var(--text3)'}}>
                        {v.country} · {v.page}
                      </span>
                    ))}
                  </div>

                  {/* Summary cards - visits only */}
                  <div className="stats-grid" style={{marginBottom:28}}>
                    {/* DUE numeri, non uno (2026-09-02). Il grande e' quante
                        PERSONE: sessioni distinte di oggi. Il piccolo accanto e'
                        quante PAGINE hanno visto in tutto — todayVisits si
                        incrementa a ogni cambio di rotta della SPA, quindi da
                        solo faceva sembrare 500 persone quello che oggi era
                        ~220 persone x 1,9 pagine.
                        Nessuno dei due conta chi non esegue il JavaScript: si
                        incrementano dentro POST /api/analytics/visit, che e' una
                        chiamata del bundle gia' in esecuzione. */}
                    <div className="stat-card">
                      <div className="stat-card-value">
                        {quickStats.todayVisitors ?? '—'}
                        <span style={{fontSize:13,fontWeight:600,color:'var(--text3)',marginLeft:8,fontFamily:'JetBrains Mono,monospace'}}>
                          / {quickStats.todayVisits} pagine
                        </span>
                      </div>
                      <div className="stat-card-label">📅 Oggi · persone / pagine viste</div>
                    </div>
                    {/* Iscritti di oggi (2026-09-02, richiesto dall'operatore).
                        Viene dal DATABASE, non dai contatori: e' l'unico numero
                        del pannello che non dipende dal JavaScript del
                        visitatore, quindi e' anche quello su cui si puo'
                        contare quando gli altri due sembrano strani. */}
                    <div className="stat-card">
                      <div className="stat-card-value">{quickStats.registrazioniOggi ?? '—'}</div>
                      {/* SENZA StackSocial (2026-09-06). Chi entra con un
                          codice ha gia' pagato altrove e non arriva da un
                          annuncio: dentro questo numero faceva sembrare
                          peggiore la conversione della pubblicita'. Il conto
                          di quelli da codice si vede nel riquadro dedicato
                          qui sotto, e qui accanto solo se ce ne sono oggi. */}
                      <div className="stat-card-label">
                        🧑 Iscritti oggi <span style={{opacity:.7}}>(senza StackSocial)</span>
                        {quickStats.registrazioniOggiStackSocial > 0 &&
                          ` · +${quickStats.registrazioniOggiStackSocial} da codice`}
                      </div>
                    </div>
                    {/* Paganti e incasso della stessa giornata (2026-09-04).
                        Stessa fonte degli iscritti — il database — quindi i tre
                        riquadri raccontano la stessa storia e si leggono in fila:
                        quante persone sono arrivate, quante si sono iscritte,
                        quante hanno pagato e quanto hanno portato.
                        Si contano i pagamenti COMPLETATI, non i checkout aperti:
                        e' l'unico numero che corrisponde a soldi incassati. */}
                    <div className="stat-card">
                      <div className="stat-card-value">{quickStats.pagantiOggi ?? '—'}</div>
                      <div className="stat-card-label">💳 Paganti oggi</div>
                    </div>
                    <div className="stat-card">
                      <div className="stat-card-value">
                        {quickStats.incassoOggiEur == null ? '—' : `€${quickStats.incassoOggiEur}`}
                      </div>
                      {/* "in euro" detto esplicitamente: i pagamenti arrivano
                          anche in dollari e sterline, e un totale che li somma
                          senza dirlo sarebbe un numero senza significato. */}
                      <div className="stat-card-label">💰 Incassato oggi (in euro)</div>
                    </div>
                  </div>

                  {/* ── IL CONTO STACKSOCIAL (2026-09-06) ───────────────────
                      Ogni riscatto vale una tariffa fissa: 1 TB $20, 2 TB $40.
                      Due parole non sono decorazione e non vanno tolte:
                        "lordo"      — sono soldi che StackSocial deve, prima
                                       delle tasse: non e' quello che resta;
                        "a fine mese"— oggi il guadagno MATURA, non arriva.
                      I codici usati su 1000 sono il numero che dice come sta
                      andando il contratto, ed e' quello da guardare per
                      decidere se salire a 3000. */}
                  {quickStats.stackSocial && (
                    <div style={{ marginTop: 12, marginBottom: 28 }}>
                      <div style={{
                        display:'flex', alignItems:'baseline', gap:10, flexWrap:'wrap',
                        fontSize:11, color:'var(--text3)', fontFamily:'JetBrains Mono,monospace',
                        textTransform:'uppercase', letterSpacing:'.1em', marginBottom:10,
                      }}>
                        <span>🎟️ StackSocial · lordo, incassato a fine mese</span>
                      </div>
                      <div className="stat-cards">
                        <div className="stat-card">
                          <div className="stat-card-value">{quickStats.stackSocial.oggi.utenti}</div>
                          <div className="stat-card-label">🧑 Nuovi utenti oggi</div>
                        </div>
                        <div className="stat-card">
                          <div className="stat-card-value">${quickStats.stackSocial.oggi.dollari}</div>
                          <div className="stat-card-label">💵 Maturato oggi · lordo StackSocial</div>
                        </div>
                        <div className="stat-card">
                          <div className="stat-card-value">{quickStats.stackSocial.totale.utenti}</div>
                          <div className="stat-card-label">👥 Utenti in totale</div>
                        </div>
                        <div className="stat-card">
                          <div className="stat-card-value">${quickStats.stackSocial.totale.dollari}</div>
                          <div className="stat-card-label">🏦 Totale lordo · da incassare a fine mese</div>
                        </div>
                        <div className="stat-card">
                          <div className="stat-card-value">
                            {quickStats.stackSocial.codici.usati}
                            <span style={{fontSize:13,fontWeight:600,color:'var(--text3)',marginLeft:6,fontFamily:'JetBrains Mono,monospace'}}>
                              / {quickStats.stackSocial.codici.totali}
                            </span>
                          </div>
                          <div className="stat-card-label">
                            🎟️ Codici usati · {quickStats.stackSocial.codici.percentuale}%
                          </div>
                        </div>
                      </div>
                      {/* Un'esclusione invisibile e' un'esclusione di cui
                          nessuno si fida: se qualche riscatto non entra nel
                          guadagno, il pannello lo dice sempre. */}
                      {quickStats.stackSocial.totale.esclusi > 0 && (
                        <div style={{
                          fontSize:12, color:'var(--text3)', marginTop:10, lineHeight:1.5,
                        }}>
                          Esclusi dal guadagno {quickStats.stackSocial.totale.esclusi} riscatti
                          di collaudo (personale StackCommerce e account di prova):
                          restano contati fra i codici usati, perché quei codici sono
                          usciti dal magazzino davvero.
                        </div>
                      )}
                    </div>
                  )}

                  {/* ── LE TRE CIFRE DELLO SPAZIO (2026-09-06) ──────────────
                      E' il rapporto che dice se il modello "a vita" regge, e
                      va guardato ogni settimana invece di scoprirlo dalla
                      bolletta. Si leggono in fila, e in quest'ordine:
                        quanto disco PAGHIAMO
                        quanto abbiamo PROMESSO vendendo piani a vita
                        quanto e' davvero pieno, e quanto pesa sulla promessa
                      Finche' l'ultima percentuale resta bassa si puo'
                      vendere piu' spazio di quanto se ne possieda senza
                      rischio. Un piano da 5 TB, che chi lo compra riempie
                      sul serio, e' esattamente cio' che la sposta. */}
                  {stats?.spazio && (
                    <div className="stat-cards" style={{ marginTop: 12 }}>
                      <div className="stat-card">
                        <div className="stat-card-value">{tb(stats.spazio.acquistatoBytes)}</div>
                        <div className="stat-card-label">💽 Spazio acquistato{stats.spazio.liberoBytes != null && ` · ${tb(stats.spazio.liberoBytes)} liberi`}</div>
                      </div>
                      <div className="stat-card">
                        <div className="stat-card-value">{tb(stats.spazio.impegnatoBytes)}</div>
                        <div className="stat-card-label">🤝 Impegnato dai piani a vita</div>
                      </div>
                      <div className="stat-card">
                        <div className="stat-card-value">{tb(stats.spazio.occupatoBytes)}</div>
                        <div className="stat-card-label">
                          📦 Occupato davvero
                          {stats.spazio.percentualeSuImpegnato != null && ` · ${stats.spazio.percentualeSuImpegnato}% dell'impegnato`}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Pages + geo breakdown, last day only — cheap in-memory counters, no db.json involved */}
                  <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(260px,1fr))',gap:16,marginBottom:20}}>
                    {quickStats.topPagesToday?.length > 0 && (
                      <div style={{background:'var(--bg2)',border:'1px solid var(--border)',borderRadius:12,padding:20}}>
                        <div style={{fontSize:11,color:'var(--text3)',fontFamily:'JetBrains Mono,monospace',textTransform:'uppercase',letterSpacing:'.1em',marginBottom:14}}>📄 Top Pages (oggi)</div>
                        {quickStats.topPagesToday.map(({key, count}) => (
                          <div key={key} style={{display:'flex',justifyContent:'space-between',padding:'6px 0',borderBottom:'1px solid var(--bg)'}}>
                            <span style={{fontFamily:'JetBrains Mono,monospace',fontSize:12,color:'var(--text2)'}}>{key}</span>
                            <span style={{fontFamily:'JetBrains Mono,monospace',fontSize:12,color:'var(--green)',fontWeight:700}}>{count}</span>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Provenienza dichiarata (utm_source): era nel log ma il
                        pannello non la mostrava. 'diretto' = nessun utm e
                        nessun referente; 'referente' = arrivato da un altro
                        sito senza utm. */}
                    {quickStats.topUtmToday?.length > 0 && (
                      <div className="stat-list">
                        <div className="stat-list-title">🎯 Provenienza oggi</div>
                        {quickStats.topUtmToday.map(({key, count}) => (
                          <div className="stat-row" key={key}>
                            <span>{key}</span><span>{count}</span>
                          </div>
                        ))}
                      </div>
                    )}

                    {quickStats.topCountriesToday?.length > 0 && (
                      <div style={{background:'var(--bg2)',border:'1px solid var(--border)',borderRadius:12,padding:20}}>
                        <div style={{fontSize:11,color:'var(--text3)',fontFamily:'JetBrains Mono,monospace',textTransform:'uppercase',letterSpacing:'.1em',marginBottom:14}}>🌍 Top Regions (oggi)</div>
                        {quickStats.topCountriesToday.map(({key, count}) => (
                          <div key={key} style={{display:'flex',justifyContent:'space-between',padding:'6px 0',borderBottom:'1px solid var(--bg)'}}>
                            <span style={{fontFamily:'JetBrains Mono,monospace',fontSize:12,color:'var(--text2)'}}>{key}</span>
                            <span style={{fontFamily:'JetBrains Mono,monospace',fontSize:12,color:'var(--accent)',fontWeight:700}}>{count}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Pages + geo breakdown, all-time */}
                  <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(260px,1fr))',gap:16,marginBottom:20}}>
                    {quickStats.topPages?.length > 0 && (
                      <div style={{background:'var(--bg2)',border:'1px solid var(--border)',borderRadius:12,padding:20}}>
                        <div style={{fontSize:11,color:'var(--text3)',fontFamily:'JetBrains Mono,monospace',textTransform:'uppercase',letterSpacing:'.1em',marginBottom:14}}>📄 Top Pages (totale)</div>
                        {quickStats.topPages.map(({key, count}) => (
                          <div key={key} style={{display:'flex',justifyContent:'space-between',padding:'6px 0',borderBottom:'1px solid var(--bg)'}}>
                            <span style={{fontFamily:'JetBrains Mono,monospace',fontSize:12,color:'var(--text2)'}}>{key}</span>
                            <span style={{fontFamily:'JetBrains Mono,monospace',fontSize:12,color:'var(--green)',fontWeight:700}}>{count}</span>
                          </div>
                        ))}
                      </div>
                    )}

                    {quickStats.topCountries?.length > 0 && (
                      <div style={{background:'var(--bg2)',border:'1px solid var(--border)',borderRadius:12,padding:20}}>
                        <div style={{fontSize:11,color:'var(--text3)',fontFamily:'JetBrains Mono,monospace',textTransform:'uppercase',letterSpacing:'.1em',marginBottom:14}}>🌍 Top Regions (totale)</div>
                        {quickStats.topCountries.map(({key, count}) => (
                          <div key={key} style={{display:'flex',justifyContent:'space-between',padding:'6px 0',borderBottom:'1px solid var(--bg)'}}>
                            <span style={{fontFamily:'JetBrains Mono,monospace',fontSize:12,color:'var(--text2)'}}>{key}</span>
                            <span style={{fontFamily:'JetBrains Mono,monospace',fontSize:12,color:'var(--accent)',fontWeight:700}}>{count}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div style={{color:'var(--text3)',fontSize:12}}>
                    Statistiche storiche (visite totali, grafici 30gg, top referrer) nella tab 📊 Stats.
                  </div>
                </>
              )}
            </div>
          )}

          {/* ── STATS TAB ── */}
          {tab === 'stats' && (
            <div className="admin-section">
              {statsLoading && !stats ? (
                <div style={{color:'var(--text3)',padding:40,textAlign:'center'}}>Loading stats…</div>
              ) : stats && (
                <>
                  <div style={{display:'flex',justifyContent:'flex-end',marginBottom:12}}>
                    <button className="btn-refresh" disabled={statsLoading} onClick={() => loadStatsTab(true)}>
                      {statsLoading ? '…' : '↻ Refresh'}
                    </button>
                  </div>

                  <div className="stats-grid">
                    {[
                      ['👥 Total Users', stats.totalUsers],
                      ['✅ Active', stats.activeUsers],
                      ['🚫 Banned', stats.bannedUsers],
                      ['💾 Files', stats.totalFiles],
                      ['📦 Storage Used', formatBytes(stats.totalStorageUsed)],
                      ['💰 Revenue', `€${stats.totalRevenue || 0}`],
                      ['💳 Payments', stats.completedPayments || 0],
                      ['⭐ Paid users', stats.paidUsers || 0],
                      // Media sui PAGANTI, non su tutti gli utenti: i gratuiti
                      // sono la maggioranza e occupano quasi niente, quindi una
                      // media generale direbbe molto meno di quanto costa un
                      // cliente vero. Nel divisore restano anche i paganti che
                      // non hanno ancora caricato niente: fanno parte del conto.
                      ['📊 GB medi per cliente pagante', stats.avgStoragePerPaidUser
                        ? formatBytes(stats.avgStoragePerPaidUser) : '—'],
                      ['⏳ In 2TB welcome window', stats.usersInPromo2tbWindow ?? 0],
                      ['🎉 Paid 2TB in window', stats.paid2tbWhileInWindow ?? 0],
                    ].map(([label, value]) => (
                      <div key={label} className="stat-card">
                        <div className="stat-card-value">{value}</div>
                        <div className="stat-card-label">{label}</div>
                      </div>
                    ))}

                    <div className="stat-card wide">
                      <div className="stat-card-label">Users by Plan</div>
                      <div className="plan-breakdown">
                        {Object.entries(stats.byPlan).map(([plan, count]) => (
                          <div key={plan} className="plan-bar-item">
                            <span style={{ color: PLAN_LABEL_COLORS[plan] }}>{PLAN_EMOJIS[plan]} {plan}</span>
                            <div className="plan-bar-wrap">
                              <div className="plan-bar" style={{ width: `${stats.totalUsers ? (count/stats.totalUsers*100) : 0}%`, background: PLAN_BAR_COLORS[plan] }} />
                            </div>
                            <span className="plan-bar-count">{count}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {stats.bySource && (
                      <div className="stat-card wide">
                        <div className="stat-card-label">Registrations by Source</div>
                        <div className="plan-breakdown">
                          {Object.entries(stats.bySource)
                            .sort((a, b) => b[1].total - a[1].total)
                            .map(([source, { total, paid }]) => (
                              <div key={source} className="plan-bar-item">
                                <span style={{ color: 'var(--accent)' }}>{source}</span>
                                <div className="plan-bar-wrap">
                                  <div className="plan-bar" style={{ width: `${stats.totalUsers ? (total/stats.totalUsers*100) : 0}%`, background: 'var(--accent-fill)' }} />
                                </div>
                                <span className="plan-bar-count">{total} <span style={{color:'var(--muted)',fontWeight:400}}>({paid} paid)</span></span>
                              </div>
                            ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {analyticsAgg && (
                    <>
                      <div className="stats-grid" style={{margin:'20px 0'}}>
                        <div className="stat-card">
                          <div className="stat-card-value">{analyticsAgg.totalVisits}</div>
                          <div className="stat-card-label">👁 Total Visits</div>
                        </div>
                      </div>

                      {/* Chart — visits last 30 days */}
                      <div style={{background:'var(--bg2)',border:'1px solid var(--border)',borderRadius:12,padding:24,marginBottom:20}}>
                        <div style={{fontSize:11,color:'var(--text3)',fontFamily:'JetBrains Mono,monospace',textTransform:'uppercase',letterSpacing:'.1em',marginBottom:16}}>Visits — last 30 days</div>
                        <div style={{display:'flex',alignItems:'flex-end',gap:3,height:100}}>
                          {analyticsAgg.last30.map((d,i) => {
                            const max = Math.max(...analyticsAgg.last30.map(x=>x.visits),1);
                            const h = Math.max(2, (d.visits/max)*100);
                            // I giorni PRECEDENTI all'attivazione del filtro includono
                            // ancora il traffico interno. Vanno marcati, altrimenti il
                            // calo del giorno dopo sembra un calo di traffico vero e si
                            // finisce per giudicare gli ads su un confronto falso.
                            const gonfio = filtroDal && d.date < filtroDal;
                            const colore = gonfio ? 'rgba(20,128,74,.22)' : 'rgba(20,128,74,.5)';
                            const stacco = gonfio && analyticsAgg.last30[i+1] && analyticsAgg.last30[i+1].date >= filtroDal;
                            return (
                              <div key={i} title={`${d.date}: ${d.visits} visite${gonfio ? ' — include traffico interno' : ''}`}
                                style={{flex:1,height:`${h}%`,background:colore,borderRadius:'2px 2px 0 0',cursor:'pointer',transition:'background .15s',
                                  borderRight: stacco ? '2px dashed var(--text3)' : undefined}}
                                onMouseEnter={e=>e.target.style.background='var(--accent)'}
                                onMouseLeave={e=>e.target.style.background=colore}
                              />
                            );
                          })}
                        </div>
                        <div style={{display:'flex',justifyContent:'space-between',marginTop:6,fontSize:10,color:'var(--text3)',fontFamily:'JetBrains Mono,monospace'}}>
                          <span>{analyticsAgg.last30[0]?.date}</span>
                          <span>{analyticsAgg.last30[29]?.date}</span>
                        </div>
                        {filtroDal && (
                          <div style={{display:'flex',alignItems:'center',gap:8,marginTop:10,fontSize:11,lineHeight:1.5,color:'var(--text3)'}}>
                            <span style={{display:'inline-block',width:14,height:10,background:'rgba(20,128,74,.22)',borderRight:'2px dashed var(--text3)',flexShrink:0}} />
                            <span>Prima del {filtroDal} i totali includono il traffico interno: il confronto con i giorni successivi non è alla pari.</span>
                          </div>
                        )}
                      </div>

                      {/* Registrations last 30 days */}
                      <div style={{background:'var(--bg2)',border:'1px solid var(--border)',borderRadius:12,padding:24,marginBottom:20}}>
                        <div style={{fontSize:11,color:'var(--text3)',fontFamily:'JetBrains Mono,monospace',textTransform:'uppercase',letterSpacing:'.1em',marginBottom:16}}>New Users — last 30 days</div>
                        <div style={{display:'flex',alignItems:'flex-end',gap:3,height:80}}>
                          {analyticsAgg.last30.map((d,i) => {
                            const max = Math.max(...analyticsAgg.last30.map(x=>x.users),1);
                            const h = Math.max(2, (d.users/max)*80);
                            return (
                              <div key={i} title={`${d.date}: ${d.users} users`}
                                style={{flex:1,height:`${h}%`,background:'rgba(124,58,237,.5)',borderRadius:'2px 2px 0 0',cursor:'pointer',transition:'background .15s'}}
                                onMouseEnter={e=>e.target.style.background='var(--preview)'}
                                onMouseLeave={e=>e.target.style.background='rgba(124,58,237,.5)'}
                              />
                            );
                          })}
                        </div>
                      </div>

                      {/* Bottom grid: referrers (pages/regions moved to the Analytics tab — cheap in-memory counters) */}
                      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(260px,1fr))',gap:16}}>

                        {/* Top referrers */}
                        {analyticsAgg.topReferrers?.length > 0 && (
                          <div style={{background:'var(--bg2)',border:'1px solid var(--border)',borderRadius:12,padding:20}}>
                            <div style={{fontSize:11,color:'var(--text3)',fontFamily:'JetBrains Mono,monospace',textTransform:'uppercase',letterSpacing:'.1em',marginBottom:14}}>🔗 Top Referrers</div>
                            {analyticsAgg.topReferrers.map(({key, count}) => (
                              <div key={key} style={{display:'flex',justifyContent:'space-between',padding:'6px 0',borderBottom:'1px solid var(--bg)'}}>
                                <span style={{fontFamily:'JetBrains Mono,monospace',fontSize:12,color:'var(--text2)'}}>{key}</span>
                                <span style={{fontFamily:'JetBrains Mono,monospace',fontSize:12,color:'var(--accent)',fontWeight:700}}>{count}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </>
              )}
            </div>
          )}

          {/* ── PAYMENTS TAB ── */}
          {tab === 'payments' && (
            <div className="admin-section">
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 16 }}>
                {Object.entries(
                  payments.filter(p => p.status === 'completed').reduce((acc, p) => {
                    const cur = p.currency || '?';
                    acc[cur] = (acc[cur] || 0) + (p.amount || 0);
                    return acc;
                  }, {})
                ).map(([cur, sum]) => (
                  <div key={cur} style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 10, padding: '10px 16px' }}>
                    <div style={{ fontSize: 11, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.05em' }}>Completed · {cur}</div>
                    <div style={{ fontSize: 20, fontWeight: 800, color: 'var(--accent)', fontVariantNumeric: 'tabular-nums' }}>{CURRENCY_SYMBOLS[cur] || ''}{sum.toFixed(2)}</div>
                  </div>
                ))}
                {(() => {
                  // Only completed sales carry a billing-address country
                  // (pending/expired never reached checkout) — those are the
                  // only ones that matter for the OSS threshold anyway.
                  const completedWithCountry = payments.filter(p => p.status === 'completed' && p.country);
                  const euCount = completedWithCountry.filter(p => EU_COUNTRIES.has(p.country)).length;
                  const nonEuCount = completedWithCountry.length - euCount;
                  const missingCountry = payments.filter(p => p.status === 'completed').length - completedWithCountry.length;
                  return (
                    <div style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 10, padding: '10px 16px' }}>
                      <div style={{ fontSize: 11, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.05em' }}>Vendite UE · extra-UE</div>
                      <div style={{ fontSize: 20, fontWeight: 800, color: 'var(--accent)', fontVariantNumeric: 'tabular-nums' }}>
                        🇪🇺 {euCount} <span style={{ color: 'var(--muted)', fontWeight: 400 }}>/</span> 🌍 {nonEuCount}
                      </div>
                      {missingCountry > 0 && (
                        <div style={{ fontSize: 10, color: 'var(--muted)', marginTop: 2 }}>{missingCountry} senza paese</div>
                      )}
                    </div>
                  );
                })()}
              </div>
              <div className="users-table-wrap">
                <table className="users-table">
                  <thead>
                    <tr><th>Date</th><th>User</th><th>Email</th><th>Plan</th><th>Amount</th><th>Status</th><th>Source</th><th>Stripe Session</th></tr>
                  </thead>
                  <tbody>
                    {payments.map((p, i) => (
                      <tr key={p.id} className={i % 2 === 1 ? 'admin-row-even' : ''}>
                        <td className="td-date">{new Date(p.createdAt).toLocaleDateString('it-IT')}</td>
                        <td>@{p.username}</td>
                        <td>
                          <span className="plan-pill" style={PLAN_PILL_STYLE[p.plan] || PLAN_PILL_STYLE.free}>
                            {PLAN_EMOJIS[p.plan]} {p.plan}
                          </span>
                        </td>
                        <td className="td-num">{CURRENCY_SYMBOLS[p.currency] || ''}{p.amount}{!p.currency && ' (?)'}</td>
                        <td>
                          <span className={`status-pill ${p.status}`}>{p.status}</span>
                        </td>
                        <td style={{ fontSize: 12, whiteSpace: 'nowrap' }}>{sourceLabel(p.source)}</td>
                        <td className="td-session">{p.stripeSessionId?.slice(0,24)}…</td>
                      </tr>
                    ))}
                    {payments.length === 0 && (
                      <tr><td colSpan="7" style={{textAlign:'center',color:'var(--muted)',padding:'32px'}}>No payments yet</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* ── POSTI (2026-09-07) ──
              Una riga per piano. I RIMASTI in grande perche' sono il numero
              che si guarda; i VENDUTI accanto, piu' piccoli, perche' spiegano
              il primo. I codici StackSocial non compaiono in nessuno dei due:
              i venduti si contano dai pagamenti Stripe, e un riscatto non ne
              crea nessuno. */}
          {tab === 'posti' && (
            <div className="admin-section">
              {!posti ? <div className="admin-loading">Caricamento…</div> : (<>
                {postiMsg && <div className="admin-msg">{postiMsg}</div>}
                <div className="stat-cards" style={{ marginBottom: 22 }}>
                  {Object.entries(posti.piani).map(([plan, p]) => (
                    <div className="stat-card" key={plan} style={{ minWidth: 260 }}>
                      <div className="stat-card-value" style={{ color: p.rimasti === 0 ? 'var(--text3)' : undefined }}>
                        {p.rimasti}
                      </div>
                      <div className="stat-card-label">
                        🪑 {plan.replace('lifetime_', '').toUpperCase()} · posti rimasti
                      </div>
                      <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 6, fontFamily: 'JetBrains Mono,monospace' }}>
                        venduti {p.venduti} · totale {p.base}
                      </div>
                      <div style={{ display: 'flex', gap: 6, marginTop: 10, flexWrap: 'wrap' }}>
                        <input
                          type="number" className="admin-input" style={{ width: 74 }}
                          value={postiInput[plan] ?? ''} placeholder="n"
                          onChange={e => setPostiInput(x => ({ ...x, [plan]: e.target.value }))}
                        />
                        <button className="btn-action gift" disabled={postiBusy}
                          onClick={() => agisciSuiPosti(plan, 'aggiungi')}>+ Aggiungi posti</button>
                        <button className="btn-action" disabled={postiBusy}
                          onClick={() => agisciSuiPosti(plan, 'imposta')}>Imposta</button>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="stat-list">
                  <div className="stat-list-title">🕘 Modifiche ai posti</div>
                  {posti.registro.length === 0 && <div className="stat-row"><span>Nessuna modifica finora</span><span/></div>}
                  {posti.registro.map(r => (
                    <div className="stat-row" key={r.id}>
                      <span>
                        {new Date(r.created_at).toLocaleString()} · {r.plan.replace('lifetime_', '').toUpperCase()} ·{' '}
                        {r.azione === 'aggiungi' ? `aggiunti ${r.valore}` : `impostati a ${r.valore}`}
                      </span>
                      {/* Separatore esplicito: senza, "impostati a 7" e "15 →"
                          finiscono attaccati e si leggono come "715". */}
                      <span>totale {r.base_prima} → {r.base_dopo} · rimasti {r.rimasti_dopo}</span>
                    </div>
                  ))}
                </div>
              </>)}
            </div>
          )}

          {/* ── STACKSOCIAL TAB ── */}
          {tab === 'stacksocial' && (
            <div className="admin-section">
              {!ssCodes ? <div className="admin-loading">Caricamento…</div> : (() => {
                const cellCount = (plan, partner, status) =>
                  ssCodes.byPlanPartner.find(r => r.plan === plan && r.partner === partner && r.status === status)?.c || 0;
                const plans = [['lifetime_1tb', '1 TB'], ['lifetime_2tb', '2 TB']];
                const partners = [['stacksocial', '🎟️ StackSocial (partner)'], ['internal', '🔧 Interni']];

                // Trend: pivot daily/weekly rows into { key -> {stacksocial, internal} }, last 21 days / 12 weeks.
                function pivotTrend(rows, keyField) {
                  const byKey = new Map();
                  for (const r of rows) {
                    if (!byKey.has(r[keyField])) byKey.set(r[keyField], { stacksocial: 0, internal: 0 });
                    byKey.get(r[keyField])[r.partner] = r.c;
                  }
                  return [...byKey.entries()].sort((a, b) => a[0].localeCompare(b[0]));
                }
                const dailyRows = ssTrend ? pivotTrend(ssTrend.daily, 'day').slice(-21) : [];
                const weeklyRows = ssTrend ? pivotTrend(ssTrend.weekly, 'weekStart').slice(-12) : [];
                const maxDaily = Math.max(1, ...dailyRows.map(([, v]) => v.stacksocial + v.internal));
                const maxWeekly = Math.max(1, ...weeklyRows.map(([, v]) => v.stacksocial + v.internal));

                return (
                  <>
                    {/* Stato dei codici */}
                    <h3 className="admin-subheading">Stato dei codici</h3>
                    <div className="admin-section stats-grid" style={{ marginBottom: 24 }}>
                      {partners.map(([pKey, pLabel]) => plans.map(([plKey, plLabel]) => {
                        const redeemed = cellCount(plKey, pKey, 'redeemed');
                        const remaining = cellCount(plKey, pKey, 'unused');
                        return (
                          <div className="stat-card" key={pKey + plKey}>
                            <div className="stat-card-value" style={{ fontSize: 26 }}>{redeemed} / {redeemed + remaining}</div>
                            <div className="stat-card-label">{pLabel} · {plLabel}</div>
                            <div style={{ fontSize: 11, color: 'var(--text3)' }}>{remaining} ancora disponibili</div>
                          </div>
                        );
                      }))}
                    </div>

                    {/* Andamento */}
                    <h3 className="admin-subheading">Andamento attivazioni</h3>
                    <div className="ss-trend-grid">
                      <div>
                        <div className="admin-modal-hint">Per giorno (ultimi 21)</div>
                        {dailyRows.map(([day, v]) => (
                          <div className="ss-trend-row" key={day}>
                            <span className="ss-trend-label">{day.slice(5)}</span>
                            <div className="ss-trend-bar-wrap">
                              <div className="ss-trend-bar" style={{ width: `${100 * (v.stacksocial + v.internal) / maxDaily}%` }} />
                            </div>
                            <span className="ss-trend-count">{v.stacksocial + v.internal}{v.internal ? ` (${v.internal} int.)` : ''}</span>
                          </div>
                        ))}
                      </div>
                      <div>
                        <div className="admin-modal-hint">Per settimana (ultime 12, da lunedì)</div>
                        {weeklyRows.map(([week, v]) => (
                          <div className="ss-trend-row" key={week}>
                            <span className="ss-trend-label">{week}</span>
                            <div className="ss-trend-bar-wrap">
                              <div className="ss-trend-bar" style={{ width: `${100 * (v.stacksocial + v.internal) / maxWeekly}%` }} />
                            </div>
                            <span className="ss-trend-count">{v.stacksocial + v.internal}{v.internal ? ` (${v.internal} int.)` : ''}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Ricerca codice singolo */}
                    <h3 className="admin-subheading" style={{ marginTop: 28 }}>Cerca un codice</h3>
                    <p className="admin-modal-hint">L'unico modo per verificare un codice singolo — quando un cliente scrive che il suo non funziona.</p>
                    <div className="admin-toolbar">
                      <input
                        className="admin-search"
                        placeholder="Incolla il codice…"
                        value={ssLookupInput}
                        onChange={e => setSsLookupInput(e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && handleCodeLookup()}
                      />
                      <button className="btn-gift" onClick={handleCodeLookup} disabled={ssLookupLoading || !ssLookupInput.trim()}>
                        {ssLookupLoading ? 'Cerco…' : '🔍 Cerca'}
                      </button>
                    </div>
                    {ssLookupResult === 'not_found' && <div className="admin-blocked-note">Nessun codice trovato con questo valore.</div>}
                    {ssLookupResult && ssLookupResult !== 'not_found' && (
                      <div className="admin-lookup-result">
                        <div><strong>Piano:</strong> {ssLookupResult.plan === 'lifetime_1tb' ? '1 TB' : '2 TB'}</div>
                        <div><strong>Origine:</strong> {ssLookupResult.partner === 'internal' ? 'Interno' : 'StackSocial (partner)'}</div>
                        <div><strong>Stato:</strong> {ssLookupResult.status === 'redeemed' ? '✅ Riscattato' : '⭕ Non ancora riscattato'}</div>
                        {ssLookupResult.status === 'redeemed' && (
                          <>
                            <div><strong>Quando:</strong> {new Date(ssLookupResult.redeemed_at).toLocaleString('it-IT')}</div>
                            <div><strong>Da:</strong> {ssLookupResult.redeemed_by_email ? `${ssLookupResult.redeemed_by_username} (${ssLookupResult.redeemed_by_email})` : '(utente eliminato)'}</div>
                          </>
                        )}
                      </div>
                    )}

                    {/* Elenco riscatti */}
                    <h3 className="admin-subheading" style={{ marginTop: 28 }}>Riscatti recenti</h3>
                    <div className="users-table">
                      <div className="users-table-head" style={{ gridTemplateColumns: '160px 90px 130px 1fr 70px' }}>
                        <div>Data e ora</div><div>Piano</div><div>Origine</div><div>Email</div><div>Codice</div>
                      </div>
                      {ssCodes.recent.map((r, i) => (
                        <div className={`user-row${i % 2 === 1 ? ' admin-row-even' : ''}`} style={{ gridTemplateColumns: '160px 90px 130px 1fr 70px' }} key={i}>
                          <div>{new Date(r.redeemed_at).toLocaleString('it-IT')}</div>
                          <div>{r.plan === 'lifetime_1tb' ? '1 TB' : '2 TB'}</div>
                          <div>{r.partner === 'internal' ? 'Interno' : 'StackSocial'}</div>
                          <div>{r.email || '—'}</div>
                          <div style={{ fontFamily: 'monospace', color: 'var(--text3)' }}>…{r.codeLast3}</div>
                        </div>
                      ))}
                    </div>
                  </>
                );
              })()}
            </div>
          )}

          {/* ── TICKETS TAB ── */}
          {tab === 'tickets' && (
            <div className="admin-section">
              <div className="users-table-wrap">
                <table className="users-table">
                  <thead>
                    <tr><th>Updated</th><th>Code</th><th>User</th><th>Subject</th><th>Status</th><th>Last from</th></tr>
                  </thead>
                  <tbody>
                    {tickets.map((t, i) => (
                      <tr key={t.id} className={i % 2 === 1 ? 'admin-row-even' : ''} style={{ cursor: 'pointer' }} onClick={() => openTicket(t.id)}>
                        <td className="td-date">{new Date(t.updatedAt).toLocaleString()}</td>
                        <td style={{ fontFamily: "'JetBrains Mono',monospace" }}>{t.code}</td>
                        <td>@{t.username}</td>
                        <td>{t.subject}</td>
                        <td><span className={`status-pill ${t.status}`}>{t.status.replace('_', ' ')}</span></td>
                        <td>{t.lastMessageFrom === 'admin' ? 'You' : 'User'}</td>
                      </tr>
                    ))}
                    {tickets.length === 0 && (
                      <tr><td colSpan="6" style={{textAlign:'center',color:'var(--muted)',padding:'32px'}}>No tickets yet</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {/* Ticket Modal */}
      {ticketTarget && (
        <div className="modal-overlay" onClick={() => setTicketTarget(null)}>
          <div className="modal modal-wide" onClick={e => e.stopPropagation()}>
            {ticketTarget.loading ? (
              <p style={{ color: 'var(--text3)' }}>Loading…</p>
            ) : (
              <>
                <h3>{ticketTarget.code} — {ticketTarget.subject}</h3>
                <p style={{ margin: '-10px 0 0', fontSize: 13, color: 'var(--text3)' }}>
                  @{ticketTarget.username} · {ticketTarget.email} · <span className={`status-pill ${ticketTarget.status}`}>{ticketTarget.status.replace('_', ' ')}</span>
                </p>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {TICKET_STATUSES.map(([value, label]) => (
                    <button
                      key={value}
                      className="btn-cancel"
                      disabled={ticketBusy || ticketTarget.status === value}
                      onClick={() => handleTicketStatusChange(value)}
                      style={{ opacity: ticketTarget.status === value ? 0.5 : 1, fontSize: 12, padding: '4px 10px' }}
                    >
                      {label}
                    </button>
                  ))}
                </div>
                <div className="ticket-thread" style={{ maxHeight: 320, overflowY: 'auto' }}>
                  {ticketTarget.messages.map(m => (
                    <div key={m.id} className={`ticket-msg from-${m.from}`}>
                      <div className="ticket-msg-meta">{m.from === 'admin' ? 'You' : `@${ticketTarget.username}`} · {new Date(m.createdAt).toLocaleString()}</div>
                      {m.body}
                      <AllegatiMessaggio ticketId={ticketTarget.id} attachments={m.attachments} />
                      {m.allegatiRimossiQuanti > 0 && (
                        <div className="ticket-att-scaduti">
                          📎 {m.allegatiRimossiQuanti} allegato/i rimosso/i per scadenza (90 giorni dalla chiusura)
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                <textarea
                  className="ticket-textarea" placeholder="Write the solution / reply…"
                  value={ticketReply} onChange={e => setTicketReply(e.target.value)}
                />
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--text2)' }}>
                  <input type="checkbox" checked={ticketClose} onChange={e => setTicketClose(e.target.checked)} />
                  Mark as resolved (closes the ticket)
                </label>
                <div className="modal-actions">
                  <button className="btn-cancel" onClick={() => setTicketTarget(null)}>Close</button>
                  <button className="btn-gift" onClick={handleTicketReply} disabled={ticketBusy || !ticketReply.trim()}>
                    {ticketBusy ? 'Sending…' : ticketClose ? '✅ Send & resolve' : '💬 Send reply'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
